<template>
    <v-container fluid class="pa-8 bg-grey-lighten-5 h-100">
        <div class="d-flex justify-space-between align-center mb-6">
            <div>
                <h2 class="text-h4 font-weight-bold text-grey-darken-3">Few-shot 관리</h2>
                <p class="text-subtitle-1 text-grey-darken-1 mt-1">AI가 참고할 답변 예시를 미리 등록하고 관리합니다.</p>
            </div>
        </div>

        <v-card class="rounded-xl border-none shadow-soft" elevation="0" color="white">
            <div class="pa-6 d-flex justify-space-between align-center">
                <div class="d-flex align-center">
                    <v-avatar color="indigo-lighten-5" class="mr-4" size="48">
                        <v-icon color="indigo-darken-2" size="24">mdi-text-box-multiple</v-icon>
                    </v-avatar>
                    <div>
                        <h3 class="text-h6 font-weight-bold text-grey-darken-3">예제 목록</h3>
                        <span class="text-caption text-grey-darken-1">총 {{ items.length }}개의 예제가 있습니다</span>
                    </div>
                </div>
                <div class="d-flex align-center">
                    <v-select v-model="systemFilter" :items="systemOptions" label="시스템 선택" variant="outlined"
                        density="compact" hide-details class="mr-4" style="width: 180px;"
                        @update:model-value="fetchItems"></v-select>
                    <v-select v-model="filterType" :items="filterOptions" item-title="title" item-value="value"
                        label="유형 필터" variant="outlined" density="compact" class="mr-4"
                        style="width: 180px; border-radius: 12px;" hide-details bg-color="grey-lighten-5" color="indigo"
                        @update:model-value="fetchItems"></v-select>
                    <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="질문 검색" single-line
                        hide-details variant="outlined" density="compact" class="mr-4 search-field"
                        style="width: 240px; border-radius: 12px;" bg-color="grey-lighten-5" color="indigo"
                        @keyup.enter="fetchItems" @click:clear="fetchItems" clearable></v-text-field>
                    <v-btn color="indigo-darken-2" prepend-icon="mdi-plus" @click="openAddDialog" elevation="2"
                        height="40" class="px-6 text-capitalize rounded-lg font-weight-bold">
                        예제 추가
                    </v-btn>
                </div>
            </div>

            <v-divider class="border-opacity-50"></v-divider>

            <v-data-table :headers="headers" :items="items" :loading="loading" class="pa-2 modern-table" hover
                :items-per-page="10">
                <template v-slot:[typeSlot]="{ item }">
                    <v-chip size="small" :color="getTypeColor(item.raw.fewShotType)" variant="flat"
                        class="font-weight-bold">
                        {{ item.raw.typeLabel }}
                    </v-chip>
                </template>
                <template v-slot:[sysShotSlot]="{ item }">
                    <v-chip size="small" :color="getSystemColor(item.raw.sysId)" variant="flat" label>
                        {{ getSystemName(item.raw.sysId) }}
                    </v-chip>
                </template>
                <template v-slot:[useSlot]="{ item }">
                    <v-chip size="small" :color="item.raw.useYn === 'Y' ? 'success' : 'grey'" variant="flat"
                        class="font-weight-bold">
                        {{ item.raw.useYn === 'Y' ? '사용' : '미사용' }}
                    </v-chip>
                </template>
                <template v-slot:[questionSlot]="{ item }">
                    <div class="d-flex align-center py-3">
                        <v-avatar size="32" color="indigo-lighten-5" class="mr-3">
                            <v-icon size="18" color="indigo-lighten-1">mdi-help-circle-outline</v-icon>
                        </v-avatar>
                        <a href="#" @click.prevent="openEditDialog(item.raw)"
                            class="text-decoration-none font-weight-bold text-indigo-darken-2 question-link text-body-1">
                            {{ item.raw.question }}
                        </a>
                    </div>
                </template>
                <template v-slot:[fewShotCnSlot]="{ item }">
                    <div class="d-flex align-center">
                        <v-chip size="small" color="blue-grey-lighten-4" variant="flat"
                            class="mr-2 text-blue-grey-darken-3 font-weight-bold font-monospace">퓨샷내용</v-chip>
                        <div class="text-truncate text-grey-darken-2 font-monospace"
                            style="max-width: 350px; font-size: 0.85em;">
                            {{ item.raw.fewShotCn }}
                        </div>
                    </div>
                </template>
                <template v-slot:[addedTimeSlot]="{ item }">
                    <v-chip size="small" color="grey" variant="tonal" class="font-weight-medium">
                        {{ formatDate(item.raw.addedTime) }}
                    </v-chip>
                </template>
            </v-data-table>
        </v-card>

        <!-- Add/Edit Dialog -->
        <v-dialog v-model="dialog" width="80%" persistent>
            <v-card class="rounded-xl shadow-soft" style="height: 80vh; min-height: 700px;">
                <v-card-title class="pa-6 d-flex justify-space-between align-center border-bottom bg-grey-lighten-5">
                    <span class="text-h5 font-weight-bold text-grey-darken-3">{{ isEditMode ? '예제 수정' : '새 예제 추가'
                        }}</span>
                    <v-btn icon="mdi-close" variant="text" color="grey" @click="closeDialog"
                        density="comfortable"></v-btn>
                </v-card-title>

                <v-divider></v-divider>

                <v-card-text class="pa-6">
                    <v-row>
                        <v-col cols="12" md="4">
                            <label class="text-subtitle-2 font-weight-bold text-grey-darken-2 mb-2 d-block">대상
                                시스템</label>
                            <v-select v-model="editedItem.sysId" :items="systemOptions.filter(o => o.value !== 'ALL')"
                                variant="outlined" density="comfortable" color="indigo" class="rounded-lg"
                                hide-details></v-select>
                        </v-col>
                        <v-col cols="12" md="4">
                            <label class="text-subtitle-2 font-weight-bold text-grey-darken-2 mb-2 d-block">유형
                                (Type)</label>
                            <v-select v-model="editedItem.fewShotType" :items="typeOptions" item-title="title"
                                item-value="value" variant="outlined" density="comfortable" color="indigo"
                                class="rounded-lg" hide-details></v-select>
                        </v-col>
                        <v-col cols="12" md="4">
                            <label class="text-subtitle-2 font-weight-bold text-grey-darken-2 mb-2 d-block">사용여부 (Use
                                Y/N)</label>
                            <v-select v-model="editedItem.useYn" :items="[
                                { title: '사용 (Y)', value: 'Y' },
                                { title: '미사용 (N)', value: 'N' }
                            ]" item-title="title" item-value="value" variant="outlined" density="comfortable"
                                color="indigo" class="rounded-lg" hide-details></v-select>
                        </v-col>
                        <v-col cols="12">
                            <label class="text-subtitle-2 font-weight-bold text-grey-darken-2 mb-2 d-flex align-center">
                                질문 (Question)
                                <v-btn v-if="!isEditingQuestion && isEditMode" icon="mdi-pencil" variant="text"
                                    density="compact" size="small" color="indigo" class="ml-2"
                                    @click="isEditingQuestion = true" title="질문 수정"></v-btn>
                            </label>

                            <!-- View Mode (Truncated) -->
                            <div v-if="!isEditingQuestion && isEditMode"
                                class="pa-4 bg-grey-lighten-4 rounded-lg text-body-1 text-grey-darken-3 border cursor-pointer"
                                style="border-color: #e0e0e0 !important; min-height: 56px;"
                                @click="isEditingQuestion = true">
                                {{ truncatedQuestion }}
                                <v-tooltip activator="parent" location="top" max-width="500"
                                    content-class="bg-grey-darken-4">
                                    {{ editedItem.question }}
                                </v-tooltip>
                            </div>

                            <!-- Edit Mode (Textarea) -->
                            <v-textarea v-else v-model="editedItem.question" placeholder="예: 이번 달 매출은 얼마인가요?"
                                variant="outlined" density="comfortable" color="indigo" hide-details="auto"
                                class="rounded-lg" rows="3" auto-grow :autofocus="isEditingQuestion"
                                @blur="onQuestionBlur"></v-textarea>
                        </v-col>
                        <v-col cols="12">
                            <label class="text-subtitle-2 font-weight-bold text-grey-darken-2 mb-2 d-block">퓨샷내용</label>
                            <v-textarea v-model="editedItem.fewShotCn" placeholder="퓨샷 내용을 입력해주세요." variant="outlined"
                                rows="8" color="indigo" hide-details="auto" class="font-monospace rounded-lg"
                                bg-color="grey-lighten-5"></v-textarea>
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="pa-6 bg-grey-lighten-5">
                    <v-btn variant="outlined" color="error" @click="deleteItem" class="px-6 rounded-lg mr-auto"
                        height="40" v-if="isEditMode">
                        삭제
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn variant="outlined" color="grey-darken-1" @click="closeDialog" class="px-6 rounded-lg mr-2"
                        height="40">
                        취소
                    </v-btn>
                    <v-btn color="indigo-darken-2" @click="saveItem" class="px-6 rounded-lg" elevation="2" height="40">
                        {{ isEditMode ? '수정하기' : '저장하기' }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script setup>
import { ref, onMounted, reactive, computed, getCurrentInstance } from 'vue';
import { getSqlModel } from '@/utils/modelSettings';

const { proxy } = getCurrentInstance();
const axios = proxy.$axios;

const systemFilter = ref('ALL');
const systemOptions = ref([
    { title: '전체 시스템', value: 'ALL' },
]);

const fetchSystemInfo = async () => {
    try {
        const res = await axios.post('/common/sys-info', {});
        if (res.data.status === 'success') {
            const list = res.data.data.list;
            systemOptions.value = [
                { title: '전체 시스템', value: 'ALL' },
                ...list.map(s => ({ title: s.sysNm, value: s.sysId }))
            ];
        }
    } catch (err) {
        console.error('Failed to fetch system info:', err);
    }
};

const getSystemName = (sysId) => {
    const option = systemOptions.value.find(o => o.value === sysId);
    return option ? option.title : sysId;
};

const systemColors = ['blue-darken-2', 'teal-darken-1', 'deep-purple-darken-1', 'orange-darken-2', 'cyan-darken-2'];

const getSystemColor = (sysId) => {
    const index = systemOptions.value.findIndex(option => option.value === sysId);
    return index > 0 ? systemColors[(index - 1) % systemColors.length] : 'grey';
};

const getDefaultSystemId = () => systemOptions.value.find(option => option.value !== 'ALL')?.value || null;

const editedIndex = ref(-1);
const isEditingQuestion = ref(false);

const editedItem = reactive({
    fewShotId: null,
    question: '',
    fewShotCn: '',
    fewShotType: 'rag_shot_sql',
    useYn: 'Y',
    sysId: null
});

const isEditMode = computed(() => editedIndex.value > -1);

const headers = [
    { title: '유형', key: 'fewShotType', align: 'start', width: '8%', sortable: true },
    { title: '시스템', key: 'sysId', align: 'start', width: '10%', sortable: true },
    { title: '사용여부', key: 'useYn', align: 'start', width: '8%', sortable: true },
    { title: '질문', key: 'question', align: 'start', width: '30%', sortable: true },
    { title: '퓨샷내용', key: 'fewShotCn', align: 'start', width: '32%', sortable: false },
    { title: '추가 시간', key: 'addedTime', align: 'end', width: '12%', sortable: true },
];

// Slot names
const questionSlot = 'item.question';
const fewShotCnSlot = 'item.fewShotCn';
const addedTimeSlot = 'item.addedTime';
const typeSlot = 'item.fewShotType';
const useSlot = 'item.useYn';
const sysShotSlot = 'item.sysId';

const dialog = ref(false);
const loading = ref(false);
const search = ref('');
const items = ref([]);

const typeOptions = [
    { title: 'SQL', value: 'rag_shot_sql' },
    { title: '세그먼트', value: 'rag_shot_segment' }
];

const filterType = ref('ALL');
const filterOptions = [
    { title: '전체 유형', value: 'ALL' },
    ...typeOptions
];

const openAddDialog = () => {
    editedItem.fewShotId = null;
    editedItem.question = '';
    editedItem.fewShotCn = '';
    editedItem.fewShotType = 'rag_shot_sql';
    editedItem.useYn = 'Y';
    editedItem.sysId = systemFilter.value !== 'ALL' ? systemFilter.value : getDefaultSystemId();
    isEditingQuestion.value = true; // Always edit mode for new
    dialog.value = true;
};

const openEditDialog = (item) => {
    editedIndex.value = items.value.indexOf(item);
    editedItem.fewShotId = item.id; // Map internal id
    editedItem.question = item.question;
    editedItem.fewShotCn = item.fewShotCn;
    editedItem.fewShotType = item.fewShotType || 'rag_shot_sql';
    editedItem.useYn = item.useYn || 'Y';
    editedItem.sysId = item.sysId || getDefaultSystemId();
    isEditingQuestion.value = false; // View mode initially
    dialog.value = true;
};

const closeDialog = () => {
    dialog.value = false;
    editedIndex.value = -1;
    editedItem.fewShotId = null;
    editedItem.question = '';
    editedItem.fewShotCn = '';
    editedItem.fewShotType = 'rag_shot_sql';
    editedItem.useYn = 'Y';
    editedItem.sysId = getDefaultSystemId();
};

const fetchItems = () => {
    loading.value = true;

    const params = {};
    if (filterType.value !== 'ALL') {
        params.fewShotType = filterType.value;
    }
    if (systemFilter.value !== 'ALL') {
        params.sysId = systemFilter.value;
    }
    if (search.value) {
        params.question = search.value;
    }

    axios.post('/rag/few-shot/list', params, {
        headers: { 'Content-Type': 'application/json' }
    })
        .then(res => {
            if (res.data.status === 'success') {
                console.log('Few-shot list loaded:', res.data.data.list);
                const list = res.data.data.list || [];
                items.value = list.map(item => ({
                    id: item.fewShotId,
                    question: item.question,
                    fewShotCn: item.fewShotCn,
                    fewShotType: item.fewShotType,
                    useYn: item.useYn,
                    sysId: item.sysId,
                    typeLabel: getTypeLabel(item.fewShotType),
                    addedTime: item.crtDt ? parseDate(item.crtDt) : null
                }));
            }
        })
        .catch(err => {
            console.error(err);
            alert('데이터 로드 실패');
        })
        .finally(() => {
            loading.value = false;
        });
};

const saveItem = () => {
    if (!editedItem.question || !editedItem.fewShotCn) {
        alert('질문과 퓨샷내용을 모두 입력해주세요.');
        return;
    }

    loading.value = true;

    const payload = {
        fewShotId: editedItem.fewShotId,
        question: editedItem.question,
        fewShotCn: editedItem.fewShotCn,
        fewShotType: editedItem.fewShotType,
        useYn: editedItem.useYn,
        sysId: editedItem.sysId,
        sqlModel: getSqlModel()
    };

    axios.post('/rag/few-shot/save', payload, {
        headers: { 'Content-Type': 'application/json' }
    })
        .then(res => {
            if (res.data.status === 'success') {
                alert('저장되었습니다.');
                closeDialog();
                fetchItems();
            } else {
                alert('저장 실패: ' + res.data.message);
            }
        })
        .catch(err => {
            console.error(err);
            alert('오류가 발생했습니다.');
        })
        .finally(() => {
            loading.value = false;
        });
};

const deleteItem = () => {
    if (!confirm('정말 이 예제를 삭제하시겠습니까?')) return;

    loading.value = true;
    axios.post('/rag/few-shot/delete', { fewShotId: editedItem.fewShotId }, {
        headers: { 'Content-Type': 'application/json' }
    })
        .then(res => {
            if (res.data.status === 'success') {
                alert('삭제되었습니다.');
                closeDialog();
                fetchItems();
            } else {
                alert('삭제 실패: ' + res.data.message);
            }
        })
        .catch(err => {
            console.error(err);
            alert('오류가 발생했습니다.');
        })
        .finally(() => {
            loading.value = false;
        });
};

const parseDate = (val) => {
    if (!val) return null;
    return new Date(val); // Standard JS Date parsing handles ISO strings
};

const formatDate = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    }).format(date);
};

onMounted(() => {
    fetchSystemInfo();
    fetchItems();
});

const getTypeLabel = (type) => {
    const option = typeOptions.find(o => o.value === type);
    return option ? option.title : type;
};

const getTypeColor = (type) => {
    return type === 'rag_shot_sql' ? 'indigo-darken-2' : 'teal-darken-1';
};

const truncatedQuestion = computed(() => {
    if (!editedItem.question) return '';
    if (editedItem.question.length <= 100) return editedItem.question;
    return editedItem.question.substring(0, 100) + '...';
});

const onQuestionBlur = () => {
    // Optional: Auto-switch back to view mode if needed, but user might want to stay in edit.
    // Let's keep it in edit mode until saved or closed, or manually toggled?
    // Actually, for "Edit Popup", usually fields stay editable.
    // The user ONLY complained about "viewing is uncomfortable because it's cut off" 
    // AND "show ... if > 100".
    // So the "View" mode is the solution.
    // If I switch back on blur, it might be annoying if they just clicked away.
    // But if I DON'T switch back, they have to close/reopen to see the truncated view again?
    // I'll leave it as stays-in-edit-mode.
    // Wait, if they save, it closes anyway.
};
</script>

<style scoped>
.font-monospace {
    font-family: 'Consolas', 'Monaco', monospace !important;
}

.question-link {
    transition: color 0.2s;
}

.question-link:hover {
    color: #1a237e !important;
    /* indigo-darken-4 */
    text-decoration: underline !important;
}

.shadow-soft {
    box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.05) !important;
}

.modern-table :deep(thead tr th) {
    background-color: #283593 !important;
    color: #FFFFFF !important;
    font-weight: 700 !important;
    font-size: 0.85rem !important;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    height: 56px !important;
}

.modern-table :deep(tbody tr:nth-of-type(2n)) {
    background-color: #F0F2F5 !important;
}

.modern-table :deep(tbody tr:hover) {
    background-color: #E3F2FD !important;
}

.modern-table :deep(tbody tr td) {
    padding-top: 16px !important;
    padding-bottom: 16px !important;
    border-bottom: none !important;
}

.search-field :deep(.v-field__outline__start) {
    border-radius: 12px 0 0 12px !important;
}

.search-field :deep(.v-field__outline__end) {
    border-radius: 0 12px 12px 0 !important;
}
</style>
